import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TASK_LABELS, parseWordsText } from "./toefl-mock1/readingData";
import * as mock1Data from "./toefl-mock1/readingData";
import * as mock2Data from "./toefl-mock2/readingData";
import * as mock3Data from "./toefl-mock3/readingData";
import * as mock4Data from "./toefl-mock4/readingData";
import * as mock5Data from "./toefl-mock5/readingData";
import * as mock6Data from "./toefl-mock6/readingData";
import * as mock7Data from "./toefl-mock7/readingData";
import * as mock8Data from "./toefl-mock8/readingData";
import * as mock9Data from "./toefl-mock9/readingData";
import {
  C,
  TimerBadge,
  SubmittingScreen,
  useCountdown,
  usePageTop,
  useWindowWidth,
  scrollToTop,
  routeForModule1,
  adaptiveBand,
  bandSummary,
  saveToeflResult,
} from "./toeflShared";

// ============================================================
// TOEFL iBT READING (2026 format, multistage adaptive)
// Question sets live in src/quiz/toefl-mock<N>/readingData.js
// Module 1 → (router) → harder or easier Module 2
// ============================================================

const READING_DATA = { 1: mock1Data, 2: mock2Data, 3: mock3Data, 4: mock4Data, 5: mock5Data, 6: mock6Data, 7: mock7Data, 8: mock8Data, 9: mock9Data };

const TASK_INSTRUCTIONS = {
  words: "Type the missing letters to complete each word in the paragraph.",
  daily: "Read the text and answer the questions.",
  academic: "Read the passage and answer the questions.",
};

const answerKey = (taskId, index) => `${taskId}:${index}`;

// Every scoreable item in a task: one per blank (Complete the Words)
// or one per question (Daily Life / Academic).
function taskItems(task) {
  if (task.type === "words") {
    return parseWordsText(task.text)
      .filter((p) => p.kind === "blank")
      .map((p) => ({ index: p.index, blank: p }));
  }
  return task.questions.map((q, index) => ({ index, question: q }));
}

const isAnswered = (value) => value !== undefined && String(value).trim() !== "";

function scoreTasks(tasks, answers, startNumber) {
  let correct = 0;
  let total = 0;
  const breakdown = [];

  tasks.forEach((task) => {
    taskItems(task).forEach(({ index, blank, question }) => {
      total += 1;
      const raw = answers[answerKey(task.id, index)];
      let entry;

      if (blank) {
        const typed = (raw || "").trim();
        const isCorrect = typed.toLowerCase() === blank.missing.toLowerCase();
        entry = {
          // One underscore per missing letter, e.g. "i_" for "it", "atmos_____" for "atmosphere".
          questionText: `Complete the word "${blank.shown}${"_".repeat(blank.missing.length)}" (${task.title})`,
          userAnswerText: typed ? blank.shown + typed : "No answer",
          correctAnswerText: blank.word,
          isCorrect,
        };
      } else {
        const choice = isAnswered(raw) ? Number(raw) : null;
        entry = {
          questionText: question.question,
          userAnswerText: choice === null ? "No answer" : question.options[choice],
          correctAnswerText: question.options[question.correct],
          isCorrect: choice === question.correct,
        };
        if (question.explanation) entry.explanation = question.explanation;
      }

      if (entry.isCorrect) correct += 1;
      breakdown.push({
        number: startNumber + breakdown.length,
        category: TASK_LABELS[task.type],
        ...entry,
      });
    });
  });

  return { correct, total, breakdown };
}

function ToeflReading({ mock = 1 }) {
  const { readingModules, MODULE_SECONDS } = READING_DATA[mock] || mock1Data;
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 768;
  usePageTop();

  // stage: "module1" | "break" | "module2" | "submitting"
  const [stage, setStage] = useState("module1");
  const [path, setPath] = useState(null);
  const [taskIndex, setTaskIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showText, setShowText] = useState(true);
  const module1ScoreRef = useRef(null);
  const submittedRef = useRef(false);

  const tasks = stage === "module2" ? readingModules[path] : readingModules.module1;
  const task = tasks[taskIndex] || tasks[0];
  const isLastTask = taskIndex === tasks.length - 1;
  const inModule = stage === "module1" || stage === "module2";

  useEffect(() => {
    scrollToTop();
  }, [taskIndex, stage]);

  // ── Counting answered items ───────────────────────────────────
  const countTask = (t) => {
    const items = taskItems(t);
    const answered = items.filter((it) => isAnswered(answers[answerKey(t.id, it.index)])).length;
    return { answered, total: items.length };
  };
  const moduleCounts = tasks.reduce(
    (acc, t) => {
      const c = countTask(t);
      return { answered: acc.answered + c.answered, total: acc.total + c.total };
    },
    { answered: 0, total: 0 }
  );

  const setAnswer = (taskId, index, value) => {
    if (!inModule) return;
    setAnswers((prev) => ({ ...prev, [answerKey(taskId, index)]: value }));
  };

  // ── Module transitions & submission ───────────────────────────
  const finishModule1 = useCallback(() => {
    const score = scoreTasks(readingModules.module1, answers, 1);
    module1ScoreRef.current = score;
    setPath(routeForModule1(score.correct, score.total));
    setStage("break");
  }, [answers, readingModules]);

  const submitTest = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setStage("submitting");

    const m1 = module1ScoreRef.current || scoreTasks(readingModules.module1, answers, 1);
    // Exiting during Module 1 has no route yet — derive it from Module 1.
    const route = path || routeForModule1(m1.correct, m1.total);
    const m2 = scoreTasks(readingModules[route], answers, m1.total + 1);
    const correctCount = m1.correct + m2.correct;
    const total = m1.total + m2.total;
    const percentage = Math.round((correctCount / total) * 100);
    const band = adaptiveBand(correctCount / total, route);
    const summary = bandSummary(band, route);
    const breakdown = [...m1.breakdown, ...m2.breakdown];

    const saveStatus = await saveToeflResult("TOEFL Reading Test", {
      format: "TOEFL iBT 2026",
      mock,
      rawScore: correctCount,
      total,
      percentage,
      module1: { correct: m1.correct, total: m1.total },
      module2: { correct: m2.correct, total: m2.total },
      ...summary,
      breakdown,
    });

    navigate("/quiz/toefl/result", {
      state: { correctCount, total, percentage, testType: "Reading", breakdown, ...summary, saveStatus },
    });
  }, [answers, path, navigate, mock, readingModules]);

  const handleExpire = useCallback(() => {
    if (stage === "module1") finishModule1();
    else if (stage === "module2") submitTest();
  }, [stage, finishModule1, submitTest]);

  const [timeLeft, setTimeLeft] = useCountdown(MODULE_SECONDS.module1, inModule, handleExpire);

  const startModule2 = () => {
    setTaskIndex(0);
    setShowText(true);
    setTimeLeft(MODULE_SECONDS.module2);
    setStage("module2");
  };

  const handleModuleEnd = () => {
    const unanswered = moduleCounts.total - moduleCounts.answered;
    const message =
      stage === "module1"
        ? "You cannot return to Module 1 after you continue."
        : "This will submit your Reading section.";
    if (
      unanswered > 0 &&
      !window.confirm(`You have ${unanswered} unanswered item${unanswered === 1 ? "" : "s"}. ${message} Continue anyway?`)
    ) {
      return;
    }
    if (stage === "module1") finishModule1();
    else submitTest();
  };

  // Exit ends the section early: unanswered items count as wrong and the
  // result is saved, so the score still reaches the results page and profile.
  const handleExit = () => {
    if (!window.confirm("Exit the Reading test? Your answers so far will be scored and saved, and unanswered questions will count as incorrect.")) return;
    submitTest();
  };

  const goToTask = (i) => {
    if (i < 0 || i >= tasks.length) return;
    setTaskIndex(i);
    setShowText(true);
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  if (stage === "submitting") return <SubmittingScreen isMobile={isMobile} />;

  if (stage === "break") {
    return (
      <div style={{ maxWidth: "640px", margin: "0 auto", padding: isMobile ? "100px 14px 40px" : "150px 24px 40px" }}>
        <div style={{ ...paneBase, height: "auto", padding: isMobile ? "28px 20px" : "40px 36px", textAlign: "center" }}>
          <div style={eyebrow}>TOEFL iBT • Reading</div>
          <h2 style={{ color: C.greenText, margin: "0 0 10px", fontSize: isMobile ? "1.35rem" : "1.7rem" }}>
            Module 1 complete
          </h2>
          <p style={{ color: C.muted, lineHeight: 1.7, margin: "0 0 24px" }}>
            Module 2 has been selected based on your Module 1 answers. It is timed separately
            ({Math.round(MODULE_SECONDS.module2 / 60)} minutes) and contains{" "}
            {readingModules[path].map((t) => TASK_LABELS[t.type]).join(", ")} tasks.
          </p>
          <button type="button" onClick={startModule2} style={primaryBtn}>
            Start Module 2 →
          </button>
        </div>
      </div>
    );
  }

  const moduleNumber = stage === "module1" ? 1 : 2;
  const isWords = task.type === "words";
  const taskCount = countTask(task);
  const paneHeight = isMobile ? "auto" : "calc(100vh - 300px)";

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: isMobile ? "84px 12px 130px" : "96px 24px 20px",
        background: "#ffffff",
        color: "#111",
        boxSizing: "border-box",
      }}
    >
      {/* TOOLBAR */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px", marginBottom: "10px" }}>
        <div style={{ fontSize: isMobile ? "1.1rem" : "1.4rem", color: "#555" }}>
          TOEFL iBT / <span style={{ color: C.accent, fontWeight: 600 }}>Reading</span>{" "}
          <span style={{ fontSize: "0.8em", color: C.muted }}>· Module {moduleNumber} of 2</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ ...pill, padding: "5px 12px" }}>
            {moduleCounts.answered}/{moduleCounts.total} answered
          </span>
          {!isMobile && <TimerBadge timeLeft={timeLeft} isMobile={false} />}
        </div>
      </div>

      {/* INSTRUCTIONS */}
      <div
        style={{
          background: C.soft,
          border: `1px solid ${C.border}`,
          borderLeft: `4px solid ${C.accent}`,
          borderRadius: "10px",
          padding: isMobile ? "8px 12px" : "8px 16px",
          marginBottom: "10px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: "18px",
          rowGap: "2px",
          fontSize: isMobile ? "0.78rem" : "0.84rem",
          color: "#444",
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: C.greenText }}>{TASK_LABELS[task.type]}:</strong>
        <span>{TASK_INSTRUCTIONS[task.type]}</span>
        <span>Module {moduleNumber} submits automatically when its time runs out.</span>
      </div>

      {/* MOBILE TABS */}
      {isMobile && !isWords && (
        <div style={{ display: "flex", background: "#eaf7f0", borderRadius: "10px", padding: "4px", marginBottom: "10px", gap: "4px" }}>
          {["Text", "Questions"].map((label, i) => {
            const active = (i === 0) === showText;
            return (
              <button
                key={label}
                type="button"
                onClick={() => setShowText(i === 0)}
                style={{
                  flex: 1,
                  padding: "8px",
                  borderRadius: "8px",
                  border: "none",
                  background: active ? C.accent : "transparent",
                  color: active ? "#fff" : C.accent,
                  fontWeight: 700,
                  fontSize: "0.87rem",
                  cursor: "pointer",
                }}
              >
                {label} {i === 1 && `(${taskCount.answered}/${taskCount.total})`}
              </button>
            );
          })}
        </div>
      )}

      {/* TASK */}
      {isWords ? (
        <div style={{ ...paneBase, height: paneHeight, marginBottom: "10px" }}>
          <div style={paneHeader(isMobile)}>
            <h2 style={paneTitle(isMobile)}>{task.title}</h2>
            <span style={pill}>{taskCount.answered}/{taskCount.total} words</span>
          </div>
          <div style={paneBody(isMobile)}>
            <WordsParagraph
              task={task}
              answers={answers}
              onChange={setAnswer}
              isMobile={isMobile}
            />
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "14px", marginBottom: "10px" }}>
          {(!isMobile || showText) && (
            <div style={{ ...paneBase, height: paneHeight }}>
              <div style={paneHeader(isMobile)}>
                <h2 style={paneTitle(isMobile)}>{task.title}</h2>
                <span style={pill}>{TASK_LABELS[task.type]}</span>
              </div>
              <div style={paneBody(isMobile)}>
                <div style={{ lineHeight: 1.9, fontSize: isMobile ? "0.95rem" : "1.02rem", color: "#222", whiteSpace: "pre-wrap" }}>
                  {task.text}
                </div>
              </div>
            </div>
          )}

          {(!isMobile || !showText) && (
            <div style={{ ...paneBase, height: paneHeight }}>
              <div style={paneHeader(isMobile)}>
                <h3 style={paneTitle(isMobile)}>Questions</h3>
                <span style={pill}>{taskCount.answered}/{taskCount.total} answered</span>
              </div>
              <div style={paneBody(isMobile)}>
                {task.questions.map((q, qIndex) => {
                  const last = qIndex === task.questions.length - 1;
                  const value = answers[answerKey(task.id, qIndex)];
                  return (
                    <div
                      key={qIndex}
                      style={{
                        marginBottom: last ? 0 : "1.1rem",
                        paddingBottom: last ? 0 : "1rem",
                        borderBottom: last ? "none" : "1px solid #e8f5ee",
                      }}
                    >
                      <p style={{ fontWeight: 600, margin: "0 0 8px", fontSize: isMobile ? "0.92rem" : "0.97rem", lineHeight: 1.5 }}>
                        {qIndex + 1}. {q.question}
                      </p>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {q.options.map((option, oi) => {
                          const selected = value === String(oi);
                          return (
                            <label
                              key={oi}
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "10px",
                                cursor: "pointer",
                                padding: isMobile ? "10px" : "8px 12px",
                                borderRadius: "8px",
                                background: selected ? "#e8f5ee" : "#fafffe",
                                border: `1.5px solid ${selected ? C.accent : "#dceee5"}`,
                                fontSize: isMobile ? "0.88rem" : "0.92rem",
                                color: "#222",
                                lineHeight: 1.5,
                              }}
                            >
                              <input
                                type="radio"
                                name={`q-${task.id}-${qIndex}`}
                                checked={selected}
                                onChange={() => setAnswer(task.id, qIndex, String(oi))}
                                style={{ accentColor: C.accent, width: "16px", height: "16px", flexShrink: 0, marginTop: "3px" }}
                              />
                              <span>
                                <span style={{ color: C.accent, fontWeight: 700, marginRight: "6px" }}>
                                  {String.fromCharCode(65 + oi)}.
                                </span>
                                {option}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* BOTTOM NAVIGATION */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? "10px" : 0,
          background: C.softer,
          border: `1px solid ${C.border}`,
          borderRadius: isMobile ? "12px 12px 0 0" : "10px",
          padding: isMobile ? "10px 12px" : "8px 16px",
          ...(isMobile
            ? { position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100, boxShadow: "0 -2px 12px rgba(0,0,0,0.08)" }
            : {}),
        }}
      >
        <div style={{ display: "flex", justifyContent: isMobile ? "space-between" : "flex-start", alignItems: "center", gap: "14px", order: isMobile ? 2 : 1 }}>
          <button type="button" onClick={handleExit} style={ghostBtn}>
            Exit Test
          </button>
          {isMobile && <TimerBadge timeLeft={timeLeft} isMobile />}
          {!isMobile && (
            <span style={{ fontSize: "0.84rem", color: "#555" }}>
              Task <strong style={{ color: C.greenText }}>{taskIndex + 1}</strong> of {tasks.length}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", flexWrap: "wrap", order: isMobile ? 1 : 2 }}>
          <button
            type="button"
            onClick={() => goToTask(taskIndex - 1)}
            disabled={taskIndex === 0}
            style={{
              ...navBtn(isMobile),
              background: taskIndex === 0 ? "#f0f0f0" : "#ffffff",
              color: taskIndex === 0 ? "#aaa" : C.accent,
              border: `1px solid ${C.border}`,
              cursor: taskIndex === 0 ? "not-allowed" : "pointer",
            }}
          >
            ← Prev
          </button>

          {tasks.map((t, i) => {
            const current = i === taskIndex;
            const c = countTask(t);
            const complete = c.answered === c.total;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => goToTask(i)}
                title={`${TASK_LABELS[t.type]}${complete ? " (completed)" : ""}`}
                style={{
                  width: isMobile ? "28px" : "30px",
                  height: isMobile ? "28px" : "30px",
                  borderRadius: "50%",
                  padding: 0,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  background: current ? C.accent : complete ? "#cfeedd" : "#ffffff",
                  color: current ? "#fff" : C.accent,
                  border: `1.5px solid ${current || complete ? C.accent : C.border}`,
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </button>
            );
          })}

          {!isLastTask ? (
            <button type="button" onClick={() => goToTask(taskIndex + 1)} style={{ ...navBtn(isMobile), background: C.accent, color: "#fff" }}>
              Next →
            </button>
          ) : (
            <button type="button" onClick={handleModuleEnd} style={{ ...navBtn(isMobile), background: C.greenText, color: "#fff", fontWeight: 700 }}>
              {stage === "module1" ? "Next Module →" : "Finish ✓"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Complete the Words paragraph with inline letter inputs ──────
function WordsParagraph({ task, answers, onChange, isMobile }) {
  const parts = parseWordsText(task.text);
  return (
    <p style={{ lineHeight: 2.4, fontSize: isMobile ? "1rem" : "1.1rem", color: "#222", margin: 0 }}>
      {parts.map((part, i) => {
        if (part.kind === "text") return <span key={i}>{part.value}</span>;
        const value = answers[answerKey(task.id, part.index)] || "";
        return (
          <span key={i} style={{ whiteSpace: "nowrap" }}>
            <span style={{ fontWeight: 600 }}>{part.shown}</span>
            <input
              type="text"
              value={value}
              maxLength={part.missing.length}
              // One underscore per missing letter so the length of the gap is visible.
              placeholder={"_".repeat(part.missing.length)}
              aria-label={`Missing ${part.missing.length} letter${part.missing.length === 1 ? "" : "s"} for word starting "${part.shown}"`}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={(e) => onChange(task.id, part.index, e.target.value.replace(/[^a-zA-Z]/g, ""))}
              style={{
                // Monospace + fixed spacing keeps each typed letter over its own underscore.
                width: `calc(${part.missing.length * 1.25}ch + 8px)`,
                fontFamily: "monospace",
                fontSize: "1em",
                letterSpacing: "0.25ch",
                padding: "1px 3px",
                margin: "0 2px 0 1px",
                border: "none",
                borderBottom: `2px solid ${value ? C.accent : "#9cc9b0"}`,
                background: value ? "#eaf7f0" : "#f6fbf8",
                borderRadius: "4px 4px 0 0",
                outline: "none",
                color: C.greenText,
              }}
            />
          </span>
        );
      })}
    </p>
  );
}

// ── Styles ──────────────────────────────────────────────────────
const paneBase = {
  background: "#ffffff",
  border: `1px solid ${C.border}`,
  borderRadius: "12px",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: "300px",
};

const paneHeader = (isMobile) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "8px",
  padding: isMobile ? "10px 14px" : "12px 20px",
  background: C.soft,
  borderBottom: "1px solid #dceee5",
  flexShrink: 0,
});

const paneTitle = (isMobile) => ({
  margin: 0,
  fontSize: isMobile ? "0.95rem" : "1.05rem",
  fontWeight: 700,
  color: C.greenText,
});

const paneBody = (isMobile) => ({
  flex: 1,
  minHeight: 0,
  overflowY: isMobile ? "visible" : "auto",
  padding: isMobile ? "14px" : "20px 24px",
});

const pill = {
  background: "#eaf7f0",
  border: `1px solid ${C.border}`,
  color: C.accent,
  fontWeight: 600,
  fontSize: "0.78rem",
  borderRadius: "6px",
  padding: "3px 10px",
  whiteSpace: "nowrap",
};

const eyebrow = {
  display: "inline-block",
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "1.6px",
  textTransform: "uppercase",
  color: C.green,
  border: `1px solid ${C.border}`,
  borderRadius: "50px",
  padding: "5px 14px",
  marginBottom: "14px",
};

const navBtn = (isMobile) => ({
  padding: isMobile ? "8px 14px" : "7px 16px",
  border: "none",
  borderRadius: "30px",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.86rem",
});

const ghostBtn = {
  padding: "7px 18px",
  background: "transparent",
  border: `1px solid ${C.border}`,
  color: "#555",
  borderRadius: "30px",
  cursor: "pointer",
  fontSize: "0.86rem",
};

const primaryBtn = {
  padding: "13px 32px",
  background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
  color: "#fff",
  border: "none",
  borderRadius: "50px",
  fontSize: "1rem",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 6px 16px rgba(12, 87, 46, 0.28)",
};

export default ToeflReading;
